// The CAD check: a CAD export compared with the job's items. A diff, never a re-entry.
//
// The export comes in as rows of four kinds (box, door, drawer front, sheet). Which column of
// a file holds which value is a mapping per source type, kept in settings; this file only
// knows the four kinds. ZeroGap's side of the comparison is read from the resolved job.
// The job's items stay the record: nothing here changes them.

import { calc, objectScope, type Value } from './expr.ts';
import { Rat } from './rational.ts';
import { formatLength, parseLength } from './units.ts';
import type { Library, Resolution } from './types.ts';

export type CompareKind = 'box' | 'door' | 'drawer_front' | 'sheet';
export const COMPARE_KINDS: CompareKind[] = ['box', 'door', 'drawer_front', 'sheet'];

export interface CompareRow {
  kind: CompareKind;
  /** The item's number (B-01), when the source gives one. */
  number: string;
  /** The cabinet a door or drawer front belongs to, when the source gives one. */
  parent: string;
  /** Exact decimal inches as text, or null where the source gives none. */
  width: string | null;
  height: string | null;
  depth: string | null;
  /** For a sheet row: the material's description. */
  material: string;
  /** For a sheet row: how many sheets. Otherwise how many of this piece (1 unless the file says more). */
  qty: string;
}

export interface SourceMapping {
  key: string;
  label: string;
  /** Our field → the column header in this kind of file. */
  columns: Partial<Record<'kind' | 'number' | 'parent' | 'width' | 'height' | 'depth' | 'material' | 'qty', string>>;
  /** Our kind → the words this kind of file uses for it. */
  kinds: Partial<Record<CompareKind, string[]>>;
}

// ---------- reading a file ----------

/** A small CSV reader: commas, quoted cells, doubled quotes, any line ending. */
export function readCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  const src = text.replace(/^﻿/, '');
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else quoted = false;
      } else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') {
      row.push(cell);
      cell = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(cell);
      cell = '';
      if (row.some((x) => x.trim() !== '')) rows.push(row);
      row = [];
    } else cell += c;
  }
  row.push(cell);
  if (row.some((x) => x.trim() !== '')) rows.push(row);
  return rows;
}

const norm = (s: unknown) => String(s ?? '').trim().toLowerCase();

export interface ParsedSource {
  rows: CompareRow[];
  /** Lines that could not be read, with the reason, so nothing is dropped silently. */
  skipped: { line: number; reason: string }[];
}

/** Read an export into comparison rows, using the column mapping for its source type. */
export function parseComparisonCsv(text: string, mapping: SourceMapping): ParsedSource {
  const table = readCsv(text);
  const out: ParsedSource = { rows: [], skipped: [] };
  if (table.length === 0) return out;
  const header = table[0]!.map(norm);
  const col = (field: keyof SourceMapping['columns']) => {
    const name = mapping.columns[field];
    return name ? header.indexOf(norm(name)) : -1;
  };
  const at = { kind: col('kind'), number: col('number'), parent: col('parent'), width: col('width'), height: col('height'), depth: col('depth'), material: col('material'), qty: col('qty') };
  if (at.kind < 0) {
    out.skipped.push({ line: 1, reason: `No "${mapping.columns.kind ?? 'kind'}" column in this file.` });
    return out;
  }
  const kindFor = (word: string): CompareKind | null => {
    for (const k of COMPARE_KINDS) if ((mapping.kinds[k] ?? [k]).some((w) => norm(w) === norm(word))) return k;
    return null;
  };
  const length = (cells: string[], i: number, line: number, name: string): string | null | undefined => {
    if (i < 0) return null;
    const raw = (cells[i] ?? '').trim().replace(/"$/, '');
    if (raw === '') return null;
    const v = parseLength(raw);
    if (!v) {
      out.skipped.push({ line, reason: `"${raw}" is not a ${name}.` });
      return undefined;
    }
    return v.toDecimal(6);
  };
  table.slice(1).forEach((cells, n) => {
    const line = n + 2;
    const word = (cells[at.kind] ?? '').trim();
    const kind = kindFor(word);
    if (!kind) {
      out.skipped.push({ line, reason: word === '' ? 'No kind on this line.' : `"${word}" is not a kind this source type knows.` });
      return;
    }
    const width = length(cells, at.width, line, 'width');
    const height = length(cells, at.height, line, 'height');
    const depth = length(cells, at.depth, line, 'depth');
    if (width === undefined || height === undefined || depth === undefined) return;
    const qtyRaw = at.qty >= 0 ? (cells[at.qty] ?? '').trim() : '';
    const qty = qtyRaw === '' ? Rat.ONE : Rat.tryParse(qtyRaw);
    if (!qty || qty.sign() < 0) {
      out.skipped.push({ line, reason: `"${qtyRaw}" is not a quantity.` });
      return;
    }
    out.rows.push({
      kind,
      number: at.number >= 0 ? (cells[at.number] ?? '').trim() : '',
      parent: at.parent >= 0 ? (cells[at.parent] ?? '').trim() : '',
      width, height, depth,
      material: at.material >= 0 ? (cells[at.material] ?? '').trim() : '',
      qty: qty.toDecimal(4),
    });
  });
  return out;
}

// ---------- ZeroGap's side ----------

const asValue = (v: unknown, numeric: boolean): Value => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'boolean') return v;
  if (numeric || typeof v === 'number') return parseLength(typeof v === 'number' ? v : String(v)) ?? String(v);
  return String(v);
};

/**
 * What the job's items say, in the same four kinds. An item is compared when its catalog item
 * says what to compare it as (`compare_as`, a formula giving 'box', 'door' or 'drawer_front');
 * its size is read from its inputs named width, height and depth. Sheets are the job's
 * materials whose category is one of the sheet categories.
 */
export function jobComparisonRows(res: Resolution, lib: Library, sheetCategories: string[]): CompareRow[] {
  const rows: CompareRow[] = [];
  const byId = new Map(res.items.map((i) => [i.id, i]));
  for (const item of res.items) {
    const cat = item.catalog_item_id ? lib.catalog[item.catalog_item_id] : undefined;
    const how = cat?.compare_as?.trim();
    if (!how) continue;
    const values: Record<string, Value> = {};
    for (const i of item.inputs) values[i.key] = asValue(i.value, i.data_type === 'number');
    let kind: string;
    try {
      kind = norm(calc(how, objectScope(values)) as string);
    } catch {
      continue;
    }
    if (kind !== 'box' && kind !== 'door' && kind !== 'drawer_front') continue;
    const size = (key: string) => {
      const v = values[key];
      return v instanceof Rat ? v.toDecimal(6) : null;
    };
    rows.push({
      kind,
      number: item.number,
      parent: item.parent_item_id ? byId.get(item.parent_item_id)?.number ?? '' : '',
      width: size('width'), height: size('height'), depth: size('depth'),
      material: '',
      qty: '1',
    });
  }
  const cats = new Set(sheetCategories.map(norm));
  const sheets = new Map<string, { description: string; qty: Rat }>();
  for (const g of res.groups) {
    for (const b of g.bom) {
      const m = b.material_id ? lib.materials[b.material_id] : undefined;
      if (!m || !cats.has(norm(m.category))) continue;
      const have = sheets.get(m.id) ?? { description: m.description, qty: Rat.ZERO };
      have.qty = have.qty.add(Rat.parse(b.qty));
      sheets.set(m.id, have);
    }
  }
  for (const s of sheets.values()) {
    if (s.qty.sign() <= 0) continue;
    rows.push({ kind: 'sheet', number: '', parent: '', width: null, height: null, depth: null, material: s.description, qty: s.qty.toDecimal(4) });
  }
  return rows;
}

// ---------- the diff ----------

export type DiffStatus = 'match' | 'differs' | 'only_cad' | 'only_zerogap';

export interface Difference {
  /** Identifies this exact difference, values included: a difference that changes is a new one to resolve. */
  key: string;
  status: DiffStatus;
  kind: CompareKind | 'box_count';
  label: string;
  /** Which value differs: width, height, depth, qty or count. Empty for a whole-piece line. */
  field: string;
  cad: string | null;
  zerogap: string | null;
}

export interface DiffOptions {
  /** Sizes within this many inches count as the same. Decimal text; 0 means exact. */
  toleranceInches?: string | number;
}

const expand = (rows: CompareRow[]): CompareRow[] =>
  rows.flatMap((r) => {
    if (r.kind === 'sheet') return [r];
    const n = Rat.tryParse(r.qty);
    const count = n && n.d === 1n && n.cmp(Rat.ONE) > 0 ? Math.min(Number(n.floor()), 500) : 1;
    return Array.from({ length: count }, () => ({ ...r, qty: '1' }));
  });

const show = (v: string | null) => (v === null ? '' : formatLength(v));
const KIND_WORD: Record<CompareKind, string> = { box: 'Box', door: 'Door', drawer_front: 'Drawer front', sheet: 'Sheets' };

/** Compare a CAD export with the job. Returns every line: what matches, what differs, and what is on one side only. */
export function diffComparison(cad: CompareRow[], zerogap: CompareRow[], opts: DiffOptions = {}): Difference[] {
  const tol = Rat.tryParse(opts.toleranceInches ?? 0) ?? Rat.ZERO;
  const close = (a: string | null, b: string | null) => {
    if (a === null || b === null) return a === b;
    const d = Rat.parse(a).sub(Rat.parse(b));
    return (d.sign() < 0 ? d.neg() : d).cmp(tol) <= 0;
  };
  const out: Difference[] = [];
  const add = (status: DiffStatus, kind: Difference['kind'], id: string, label: string, field: string, c: string | null, z: string | null) =>
    out.push({ key: [status, kind, id, field, c ?? '', z ?? ''].join('|'), status, kind, label, field, cad: c, zerogap: z });
  const C = expand(cad);
  const Z = expand(zerogap);

  // 1. how many boxes
  const cBoxes = C.filter((r) => r.kind === 'box');
  const zBoxes = Z.filter((r) => r.kind === 'box');
  if (cBoxes.length + zBoxes.length > 0) {
    add(cBoxes.length === zBoxes.length ? 'match' : 'differs', 'box_count', 'count', 'Number of boxes', 'count', String(cBoxes.length), String(zBoxes.length));
  }

  // 2. each box, by its number
  const left = new Map<string, CompareRow[]>();
  for (const r of zBoxes) left.set(norm(r.number), [...(left.get(norm(r.number)) ?? []), r]);
  for (const c of cBoxes) {
    const z = left.get(norm(c.number))?.shift();
    const name = `Box ${c.number || '(no number)'}`;
    if (!z) {
      add('only_cad', 'box', norm(c.number), name, '', [show(c.width), show(c.height), show(c.depth)].filter(Boolean).join(' × ') || null, null);
      continue;
    }
    let same = true;
    for (const f of ['width', 'height', 'depth'] as const) {
      if (c[f] === null) continue; // the export does not give this value, so there is nothing to compare
      if (!close(c[f], z[f])) {
        same = false;
        add('differs', 'box', norm(c.number), name, f, show(c[f]), z[f] === null ? null : show(z[f]));
      }
    }
    if (same) add('match', 'box', norm(c.number), name, '', null, null);
  }
  for (const rest of left.values()) {
    for (const z of rest) add('only_zerogap', 'box', norm(z.number), `Box ${z.number || '(no number)'}`, '', null, [show(z.width), show(z.height), show(z.depth)].filter(Boolean).join(' × ') || null);
  }

  // 3. doors and drawer fronts: by cabinet where the export says which cabinet, across the job where it does not
  for (const kind of ['door', 'drawer_front'] as const) {
    const cs = C.filter((r) => r.kind === kind);
    const zs = Z.filter((r) => r.kind === kind);
    if (cs.length + zs.length === 0) continue;
    const byCabinet = cs.length > 0 && cs.every((r) => r.parent.trim() !== '');
    const groupOf = (r: CompareRow) => (byCabinet ? norm(r.parent) : '');
    const size = (r: CompareRow) => [show(r.width), show(r.height)].join(' × ');
    const order = (a: CompareRow, b: CompareRow) => Rat.parse(a.width ?? '0').cmp(Rat.parse(b.width ?? '0')) || Rat.parse(a.height ?? '0').cmp(Rat.parse(b.height ?? '0'));
    const groups = new Set([...cs, ...zs].map(groupOf));
    for (const g of [...groups].sort()) {
      const where = g === '' ? '' : ` on ${[...cs, ...zs].find((r) => groupOf(r) === g)!.parent}`;
      const name = `${KIND_WORD[kind]}${where}`;
      const cl = cs.filter((r) => groupOf(r) === g).sort(order);
      let zl = zs.filter((r) => groupOf(r) === g).sort(order);
      const unmatched: CompareRow[] = [];
      for (const c of cl) {
        const i = zl.findIndex((z) => close(c.width, z.width) && close(c.height, z.height));
        if (i >= 0) {
          zl = [...zl.slice(0, i), ...zl.slice(i + 1)];
          add('match', kind, `${g}|${size(c)}|${out.length}`, `${name} · ${size(c)}`, '', null, null);
        } else unmatched.push(c);
      }
      // what is left on both sides is the same piece at a different size
      while (unmatched.length > 0 && zl.length > 0) {
        const c = unmatched.shift()!;
        const z = zl.shift()!;
        for (const f of ['width', 'height'] as const) {
          if (!close(c[f], z[f])) add('differs', kind, `${g}|${size(c)}|${size(z)}`, `${name} · ${size(z)}`, f, show(c[f]), show(z[f]));
        }
      }
      for (const c of unmatched) add('only_cad', kind, `${g}|${size(c)}`, `${name} · ${size(c)}`, '', size(c), null);
      for (const z of zl) add('only_zerogap', kind, `${g}|${size(z)}`, `${name} · ${size(z)}`, '', null, size(z));
    }
  }

  // 4. sheets, by material
  const total = (rows: CompareRow[]) => {
    const m = new Map<string, { name: string; qty: Rat }>();
    for (const r of rows.filter((x) => x.kind === 'sheet')) {
      const have = m.get(norm(r.material)) ?? { name: r.material, qty: Rat.ZERO };
      have.qty = have.qty.add(Rat.parse(r.qty));
      m.set(norm(r.material), have);
    }
    return m;
  };
  const cSheets = total(C);
  const zSheets = total(Z);
  const count = (q: Rat) => q.toDecimal(2).replace(/\.?0+$/, '');
  for (const [k, c] of cSheets) {
    const z = zSheets.get(k);
    const name = `Sheets · ${c.name || '(no material named)'}`;
    if (!z) add('only_cad', 'sheet', k, name, 'qty', count(c.qty), null);
    else add(c.qty.cmp(z.qty) === 0 ? 'match' : 'differs', 'sheet', k, name, 'qty', count(c.qty), count(z.qty));
  }
  for (const [k, z] of zSheets) if (!cSheets.has(k)) add('only_zerogap', 'sheet', k, `Sheets · ${z.name}`, 'qty', null, count(z.qty));

  return out;
}

/** The differences still waiting for a resolution. The CAD check cannot close while there is one. */
export function unresolvedDifferences(diffs: Difference[], resolved: { diff_key: string; resolution?: string | null }[]): Difference[] {
  const done = new Set(resolved.filter((r) => String(r.resolution ?? '').trim() !== '').map((r) => r.diff_key));
  return diffs.filter((d) => d.status !== 'match' && !done.has(d.key));
}
